/**
 * The stored records of the player-made clans and ClanDirectory, the in-memory index of them.
 *
 * Collections (deps.ts C):
 *   clans/<clanId>                   ClanRecord: name, emblem, join policy, description, leader, counters
 *   clan_names/<nameKey>             ClanNameRecord: the uniqueness index of names (like C.usernames)
 *   clan_players/<playerId>          ClanPlayerRecord: the ONE record of a player's membership (clan, role, since when)
 *                                    and of its clan cooldowns (left / kicked at, created a clan at)
 *   clan_requests/<clanId>~<playerId> ClanRequestRecord: a pending request to join
 *   clan_history/<clanId>            ClanHistoryRecord: the last config.clans.historyKept events, newest last
 *   meta/clan_seq                    { next }: the next clan id (the first player-made clan is FIRST_CLAN_ID = 101, so
 *                                    no clan id repeats the 1..14 of the original's fixed clans in old caches and logs)
 *
 * ClanDirectory keeps clans, player records and requests in memory. It loads lazily from storage (the first reader
 * waits for it) and is kept current by the clan service: every batch that writes clan documents is applied to it
 * (`apply`) right after the batch succeeded and before the service releases its locks, so a holder of `clan:<id>` sees
 * exactly the stored members of that clan, and a holder of `clanp:<playerId>` the stored requests of that player.
 * Readers of membership (leaderboards, public profiles, the assault zones and the tribute, the operator console) use
 * it instead of scanning storage; the clan service reads the documents it changes again from storage under its locks.
 *
 * The ratings are here too, so that clan.list reads no account (a list of every clan would otherwise read the account
 * of every member): a clan's rating is the sum of its members' capture counts (AccountRecord.captures), kept per clan
 * as members come and go (`apply`) and as their counts grow (`noteCaptures`: the clan service reads the capturer's
 * account after every stored capture). A member whose count is not known yet - every member after the load, a player
 * who just joined a clan, a capturer whose account could not be read after the capture (`capturesChanged`) - counts
 * its last known count (0 when none) until `settleCaptures` reads its account (once; the readers of ratings await it).
 * A capture count only ever grows (a capture adds one, nothing takes one away), so a count read late never replaces a
 * larger one. `version` changes with every change of a clan record, a membership or a member's count (the service's
 * sorted lists). Names are not here: they are the accounts', read when a clan is shown.
 */
import type { ClanBrief, ClanJoinPolicy, ClanModerationAction, ClanEventType, ClanRole, PlayerId } from '../../../shared/protocol/model';
import type { Logger } from '../logger';
import { type StorageAdapter, type StorageBatchOp, getOrSkipDamaged } from '../storage';
import { C } from './deps';

export interface ClanRecord {
  clanId: number;
  /** As its leader typed it (normalized: src/shared/clan-rules.ts). */
  name: string;
  /** clanNameKey(name): the id of its C.clanNames document. */
  nameKey: string;
  emblem: number;
  joinPolicy: ClanJoinPolicy;
  /** '' = none. */
  description: string;
  leaderId: PlayerId;
  /** Members (leader included) and officers: kept equal to the C.clanPlayers documents of the clan. */
  memberCount: number;
  officerCount: number;
  createdAt: number;
  updatedAt: number;
  /** Last rename by the leader (0 = never): the rename cooldown. */
  renamedAt: number;
  /** +1 per change. */
  version: number;
}

export interface ClanNameRecord {
  clanId: number;
  name: string;
  /** The document's own id (clanNameKey of the name). */
  key: string;
  at: number;
}

export interface ClanPlayerRecord {
  playerId: PlayerId;
  /** null = in no clan. */
  clanId: number | null;
  role: ClanRole | null;
  joinedAt: number | null;
  /** Last time the player left a clan or was removed from one (the rejoin cooldown); a disband does not count. */
  leftAt: number | null;
  /** Last time the player created a clan (the create cooldown). */
  createdClanAt: number | null;
}

export interface ClanRequestRecord {
  clanId: number;
  playerId: PlayerId;
  at: number;
}

/** A stored history event: player ids only (names are read when the history is read). */
export interface ClanEventRecord {
  seq: number;
  at: number;
  type: ClanEventType;
  actorId: PlayerId | null;
  targetId: PlayerId | null;
  zone?: number;
  stage?: number;
  zones?: number[];
  role?: ClanRole;
  from?: string;
  to?: string;
  emblem?: number;
  joinPolicy?: ClanJoinPolicy;
  description?: boolean;
  via?: ClanJoinPolicy;
  byId?: PlayerId | null;
  byClanId?: number | null;
  /** The capturer's clan name at the time (shown when that clan no longer exists). */
  byClanName?: string | null;
  action?: ClanModerationAction;
  reason?: 'account_deleted' | 'moderated';
}

export interface ClanHistoryRecord {
  clanId: number;
  /** seq of the newest event (events are numbered 1, 2, ... per clan). */
  seq: number;
  events: ClanEventRecord[];
}

/** The document of the clan id counter (in the storage's meta collection). */
export const CLAN_SEQ_COLLECTION = 'meta';
export const CLAN_SEQ_ID = 'clan_seq';
export const FIRST_CLAN_ID = 101;

export function clanRequestId(clanId: number, playerId: PlayerId): string {
  return `${clanId}~${playerId}`;
}

/** The record of a player who never was in a clan. */
export function emptyClanPlayer(playerId: PlayerId): ClanPlayerRecord {
  return { playerId, clanId: null, role: null, joinedAt: null, leftAt: null, createdClanAt: null };
}

/** Account reads settleCaptures issues at once. */
const CAPTURE_READS_AT_ONCE = 256;

const byRequestOrder = (a: ClanRequestRecord, b: ClanRequestRecord) => a.at - b.at || (a.playerId < b.playerId ? -1 : a.playerId > b.playerId ? 1 : a.clanId - b.clanId);

function copy<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

export class ClanDirectory {
  private readonly clans = new Map<number, ClanRecord>();
  private readonly players = new Map<PlayerId, ClanPlayerRecord>();
  private readonly members = new Map<number, Set<PlayerId>>();
  private readonly requestsByClan = new Map<number, Map<PlayerId, ClanRequestRecord>>();
  private readonly requestsByPlayer = new Map<PlayerId, Map<number, ClanRequestRecord>>();
  /** Known capture counts: every member's once read, and every capturer's since (noteCaptures). */
  private readonly captures = new Map<PlayerId, number>();
  /** Counts to be read (every member after the load, a member who joined, a capturer: capturesChanged): settleCaptures. */
  private readonly unknownCaptures = new Set<PlayerId>();
  /**
   * playerId -> the mark of its latest capturesChanged (marks never repeat). A read only settles a count when no
   * capture was stored since it began: a read that began before a capture may return the count from before it.
   */
  private readonly captureMarks = new Map<PlayerId, number>();
  private lastCaptureMark = 0;
  /** clanId -> the sum of its members' known counts. */
  private readonly ratings = new Map<number, number>();
  private changes = 0;
  private settling: Promise<void> | null = null;
  private loading: Promise<void> | null = null;
  private loaded = false;

  constructor(
    private readonly storage: StorageAdapter,
    private readonly logger?: Logger,
  ) {}

  /** Loads the index once (every reader awaits it). */
  ready(): Promise<void> {
    this.loading ??= this.load();
    return this.loading;
  }

  get isLoaded(): boolean {
    return this.loaded;
  }

  private async load(): Promise<void> {
    const [clans, players, requests] = await Promise.all([
      this.storage.list<ClanRecord>(C.clans),
      this.storage.list<ClanPlayerRecord>(C.clanPlayers),
      this.storage.list<ClanRequestRecord>(C.clanRequests),
    ]);
    for (const c of clans) if (c && Number.isInteger(c.clanId)) this.clans.set(c.clanId, c);
    for (const p of players) if (p && typeof p.playerId === 'string') this.setPlayer(p);
    for (const r of requests) if (r && typeof r.playerId === 'string') this.setRequest(r);
    this.loaded = true;
  }

  // ------------------------------------------------------------------------------------------------ reads (after ready)

  clan(clanId: number): ClanRecord | null {
    return this.clans.get(clanId) ?? null;
  }

  allClans(): ClanRecord[] {
    return [...this.clans.values()];
  }

  player(playerId: PlayerId): ClanPlayerRecord | null {
    return this.players.get(playerId) ?? null;
  }

  clanIdOf(playerId: PlayerId | null): number | null {
    return playerId === null ? null : (this.players.get(playerId)?.clanId ?? null);
  }

  roleOf(playerId: PlayerId): ClanRole | null {
    return this.players.get(playerId)?.role ?? null;
  }

  /** The member ids of a clan (any order). */
  membersOf(clanId: number): PlayerId[] {
    return [...(this.members.get(clanId) ?? [])];
  }

  brief(clanId: number | null): ClanBrief | null {
    const c = clanId === null ? undefined : this.clans.get(clanId);
    return c ? { clanId: c.clanId, name: c.name, emblem: c.emblem } : null;
  }

  briefOf(playerId: PlayerId | null): ClanBrief | null {
    return this.brief(this.clanIdOf(playerId));
  }

  /** Pending requests to a clan, oldest first. */
  requestsOfClan(clanId: number): ClanRequestRecord[] {
    return [...(this.requestsByClan.get(clanId)?.values() ?? [])].sort(byRequestOrder);
  }

  /** Pending requests of a player, oldest first. */
  requestsOfPlayer(playerId: PlayerId): ClanRequestRecord[] {
    return [...(this.requestsByPlayer.get(playerId)?.values() ?? [])].sort(byRequestOrder);
  }

  /** The rating of a clan: its members' captures (complete after settleCaptures). */
  ratingOf(clanId: number): number {
    return this.ratings.get(clanId) ?? 0;
  }

  /** Changes with every change of a clan record, a membership or a member's capture count. */
  get version(): number {
    return this.changes;
  }

  // ------------------------------------------------------------------------------------------------ capture counts

  /**
   * Reads the capture counts of the members whose count is not known yet (one account read each; callers that come
   * meanwhile share the reads), so that every rating is complete. Nothing to read: no storage access.
   */
  async settleCaptures(): Promise<void> {
    await this.ready();
    while (this.unknownCaptures.size > 0) {
      this.settling ??= this.readUnknownCaptures().finally(() => {
        this.settling = null;
      });
      await this.settling;
    }
  }

  private async readUnknownCaptures(): Promise<void> {
    const ids = [...this.unknownCaptures];
    // In slices: after a load every member is to be read, and a remote storage should not get them all at once.
    for (let at = 0; at < ids.length; at += CAPTURE_READS_AT_ONCE) {
      const slice = ids.slice(at, at + CAPTURE_READS_AT_ONCE);
      const marks = slice.map((id) => this.captureMark(id));
      // A damaged account counts no captures (logged) instead of failing every clan list until an operator repairs it.
      const accounts = await Promise.all(slice.map((id) => getOrSkipDamaged<{ captures?: unknown }>(this.storage, C.accounts, id, this.logger)));
      slice.forEach((id, i) => this.noteCaptures(id, Number(accounts[i]?.captures) || 0, marks[i]));
    }
  }

  /**
   * The player's capture count as its account holds it now (read after a stored capture, or by settleCaptures). A
   * count never shrinks, so a smaller one (read before a later capture) changes nothing. `mark` is the player's
   * captureMark taken before the read began: when a capture was stored since (capturesChanged), the count read may be
   * from before it, so the player stays to be read again (the loop of settleCaptures reads it once more).
   */
  noteCaptures(playerId: PlayerId, captures: number, mark: number): void {
    const count = Math.max(0, Math.floor(Number(captures) || 0));
    const known = this.captures.get(playerId);
    if (mark === this.captureMark(playerId)) this.unknownCaptures.delete(playerId);
    if (known !== undefined && count <= known) return;
    this.captures.set(playerId, count);
    const clanId = this.players.get(playerId)?.clanId ?? null;
    if (clanId === null) return;
    this.ratings.set(clanId, this.ratingOf(clanId) + count - (known ?? 0));
    this.changes++;
  }

  /**
   * The player's count changed in storage (a capture was stored): the next settleCaptures reads its account again,
   * unless noteCaptures learns the count first (from a read that began after this call). So a count whose read after
   * the capture failed is still read once. Returns the player's new captureMark (for the read that follows).
   */
  capturesChanged(playerId: PlayerId): number {
    this.unknownCaptures.add(playerId);
    const mark = ++this.lastCaptureMark;
    this.captureMarks.set(playerId, mark);
    return mark;
  }

  /** The mark of the player's latest capturesChanged (0: none): take it before reading its account. */
  captureMark(playerId: PlayerId): number {
    return this.captureMarks.get(playerId) ?? 0;
  }

  /** The account is gone (after its membership record went): its count is forgotten. */
  forgetCaptures(playerId: PlayerId): void {
    if (this.clanIdOf(playerId) === null) {
      this.captures.delete(playerId);
      this.unknownCaptures.delete(playerId);
      this.captureMarks.delete(playerId);
    }
  }

  // ------------------------------------------------------------------------------------------------ updates

  /** Applies the clan documents of a batch that was just stored (other collections are ignored). */
  apply(ops: readonly StorageBatchOp[]): void {
    for (const o of ops) {
      if (o.collection === C.clans) {
        if (o.op === 'put') this.clans.set((o.value as ClanRecord).clanId, copy(o.value as ClanRecord));
        else this.clans.delete(Number(o.id));
        this.changes++;
      } else if (o.collection === C.clanPlayers) {
        if (o.op === 'put') this.setPlayer(copy(o.value as ClanPlayerRecord));
        else this.removePlayer(o.id);
      } else if (o.collection === C.clanRequests) {
        if (o.op === 'put') this.setRequest(copy(o.value as ClanRequestRecord));
        else this.removeRequest(o.id);
      }
    }
  }

  private setPlayer(p: ClanPlayerRecord): void {
    const before = this.clanIdOf(p.playerId);
    const unknown = this.unknownCaptures.has(p.playerId);
    this.removePlayer(p.playerId);
    this.players.set(p.playerId, p);
    if (p.clanId !== null) {
      let set = this.members.get(p.clanId);
      if (!set) this.members.set(p.clanId, (set = new Set()));
      set.add(p.playerId);
      this.ratings.set(p.clanId, this.ratingOf(p.clanId) + (this.captures.get(p.playerId) ?? 0));
      // A new member's count is read once more (the load knows no count; a capture listener may have failed).
      if (unknown || before !== p.clanId) this.unknownCaptures.add(p.playerId);
      this.changes++;
    }
  }

  private removePlayer(playerId: PlayerId): void {
    const prev = this.players.get(playerId);
    if (!prev) return;
    this.players.delete(playerId);
    if (prev.clanId !== null) {
      const set = this.members.get(prev.clanId);
      set?.delete(playerId);
      if (set && set.size === 0) {
        this.members.delete(prev.clanId);
        this.ratings.delete(prev.clanId);
      } else {
        this.ratings.set(prev.clanId, this.ratingOf(prev.clanId) - (this.captures.get(playerId) ?? 0));
      }
      this.unknownCaptures.delete(playerId);
      this.changes++;
    }
  }

  private setRequest(r: ClanRequestRecord): void {
    let byClan = this.requestsByClan.get(r.clanId);
    if (!byClan) this.requestsByClan.set(r.clanId, (byClan = new Map()));
    byClan.set(r.playerId, r);
    let byPlayer = this.requestsByPlayer.get(r.playerId);
    if (!byPlayer) this.requestsByPlayer.set(r.playerId, (byPlayer = new Map()));
    byPlayer.set(r.clanId, r);
  }

  private removeRequest(id: string): void {
    const cut = id.indexOf('~');
    if (cut < 0) return;
    const clanId = Number(id.slice(0, cut));
    const playerId = id.slice(cut + 1);
    const byClan = this.requestsByClan.get(clanId);
    byClan?.delete(playerId);
    if (byClan && byClan.size === 0) this.requestsByClan.delete(clanId);
    const byPlayer = this.requestsByPlayer.get(playerId);
    byPlayer?.delete(clanId);
    if (byPlayer && byPlayer.size === 0) this.requestsByPlayer.delete(playerId);
  }
}
